import { api } from '@/api'
import { useCurrentConfiguration } from '@/features/devices/api/version'
import { t } from '@lingui/core/macro'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { BuzzStatus, LinkBuzzRequest, LinkBuzzResponse } from './models'

const BUZZ_QUERY_KEY = ['buzz']

/**
 * Gated on `=== true`: the configuration omits the key when the feature is off,
 * and `data` is `undefined` until the config resolves. Both must read as off, so
 * the card never flashes.
 */
export function useIsBuzzEnabled(): boolean {
  return useCurrentConfiguration().data?.buzz_link_enabled === true
}

export type BuzzAvailability = 'pending' | 'enabled' | 'disabled'

/**
 * The tri-state companion to `useIsBuzzEnabled()`, which collapses "off" and "not yet
 * known" into a single `false`. That collapse is right for a card that renders `null`
 * and appears late; it is wrong for a page that redirects on off, because the redirect
 * would then fire on every visit while `/configuration` is still in flight — with the
 * flag on. `isPending` is the discriminant that separates the two.
 *
 * An errored configuration resolves to `disabled` on purpose: that is the safe
 * direction, it keeps a flag-off session at zero requests to `/user/buzz`, and it
 * matches what `useIsBuzzEnabled()` already does today.
 */
export function useBuzzAvailability(): BuzzAvailability {
  const { data, isPending } = useCurrentConfiguration()

  if (isPending) return 'pending'
  return data?.buzz_link_enabled === true ? 'enabled' : 'disabled'
}

/**
 * `enabled` is threaded from the caller rather than read here, so a flag-off
 * session issues zero requests to a route that 404s.
 */
export function useBuzzBalance(options: { enabled: boolean }): UseQueryResult<BuzzStatus> {
  return useQuery({
    queryKey: BUZZ_QUERY_KEY,
    queryFn: (): Promise<BuzzStatus> => api.get('/user/buzz'),
    enabled: options.enabled,
    retry: 0,
    staleTime: 30_000
  })
}

export function useLinkBuzz(): UseMutationResult<LinkBuzzResponse, unknown, string> {
  const queryClient = useQueryClient()

  return useMutation<LinkBuzzResponse, unknown, string>({
    mutationFn: (code: string) => {
      const body: LinkBuzzRequest = { code }
      return api.post<LinkBuzzResponse>('/user/buzz/link', body)
    },
    // Load-bearing, not decoration: linking burns the code before it binds, so an
    // automatic retry re-sends a code the server already consumed and reports a
    // transient fault back to the user as a bad code.
    retry: 0,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: BUZZ_QUERY_KEY })
      toast.success(t`Hive Calls account linked`)
    }
  })
}

export function useUnlinkBuzz(): UseMutationResult<void, unknown, void> {
  const queryClient = useQueryClient()

  return useMutation<void, unknown, void>({
    mutationFn: () => api.delete<void>('/user/buzz/link'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: BUZZ_QUERY_KEY })
      toast.success(t`Hive Calls account unlinked`)
    },
    // The wallet precedent has no onError, so a failed unlink is silent and the
    // user believes it worked. Surface it.
    onError: (error) => {
      console.error('Buzz unlink error:', error)
      toast.error(t`Could not unlink your Hive Calls account. Please try again.`)
    }
  })
}
